// ─────────────────────────────────────────────────────────────────────────────
// BOQSection — Bill of Quantities section register.
// Construction Engineering Cockpit aesthetic. Token-driven, primitive-driven.
//
// Preserves:
//   - Phase 1 perf: debounced search + split enrichedSections / sectionRows memos
//   - Phase 2 perf: React.memo export wrapper
//   - All Firestore CRUD logic, dedup-on-save, omission roll-up
//   - The sparkbar weight indicator (unique to this section)
//   - The >10% overall-variance alert banner
//   - Save-error inline display with red field borders
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useEffect, useMemo, useState } from "react";
import {
  collection, deleteDoc, doc, getDocs, onSnapshot, setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";
import { useDebouncedValue } from "./useDebouncedValue";
import {
  Card, BlueprintGrid, Reveal, Eyebrow,
  Input, Select, FormRow, FormGroupDivider,
  Button, Badge,
  Stat,
  Table, Th, Td,
  Empty,
} from "./ui";

const t = tokens;

// ═════════════════════════════════════════════════════════════════════════════
// UTILITIES — preserved EXACTLY from original
// ═════════════════════════════════════════════════════════════════════════════
function numberOrZero(value) {
  const parsed = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function fmtCurrency(value) {
  return `R ${numberOrZero(value).toLocaleString(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  })}`;
}

function fmtVariance(value) {
  const n = numberOrZero(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}R ${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  })}`;
}

function normalizeBoqValue(value) {
  return String(value || "").trim().toLowerCase();
}

function buildPossibleBoqKeys(...values) {
  return values.map((v) => normalizeBoqValue(v)).filter(Boolean);
}

function buildBoqDocId(sectionNo, sectionName) {
  const no   = normalizeBoqValue(sectionNo).replace(/[^a-z0-9]+/g, "-");
  const name = normalizeBoqValue(sectionName).replace(/[^a-z0-9]+/g, "-");
  return `boq-${no || "section"}-${name || "item"}`;
}

function isApprovedVariation(variation) {
  return String(variation?.status || "").toLowerCase() === "approved";
}

function isOmissionVariation(variation) {
  const text = [
    variation?.type, variation?.voType, variation?.variationType,
    variation?.description, variation?.title, variation?.detail, variation?.scope,
  ].join(" ").toLowerCase();
  if (text.includes("omission") || text.includes("omit")) return true;
  return getVoNetAmount(variation) < 0;
}

function getVoNetAmount(variation) {
  if (!variation) return 0;
  return numberOrZero(
    variation.omissionValue ?? variation.omittedValue ?? variation.omitValue ??
    variation.netOmissionAmount ?? variation.omissionAmount ??
    variation.netVoAmount ?? variation.approvedValue ?? 0
  );
}

function getVariationOmissionAmount(item) {
  const direct = Math.abs(numberOrZero(
    item.omittedWorkAmount ?? item.omissionValue ?? item.omittedValue ??
    item.omitValue ?? item.netOmissionAmount ?? item.omissionAmount ??
    item.omittedWorkValue ?? 0
  ));
  if (direct > 0) return direct;
  const derived =
    numberOrZero(item.additionalWorkAmount ?? 0) +
    numberOrZero(item.existingRatesAmount ?? 0) -
    numberOrZero(item.omittedWorkAmount ?? 0);
  return derived < 0 ? Math.abs(derived) : 0;
}

function getSoqEngineerEstimateForSection(section, soqItems = []) {
  const sectionNo   = normalizeBoqValue(section.sectionNo);
  const sectionName = normalizeBoqValue(section.sectionName);
  return (soqItems || []).reduce((sum, item) => {
    const iNo   = normalizeBoqValue(item.sectionNo);
    const iName = normalizeBoqValue(item.sectionName);
    const matches =
      (sectionNo   && iNo   === sectionNo)   ||
      (sectionName && iName === sectionName);
    if (!matches) return sum;
    const finalQty = numberOrZero(
      item.finalEstimatedQty ?? item.finalEstimatedQuantity ??
      item.finalEstQty ?? item.finalEstimateQty ?? 0
    );
    const rate = numberOrZero(item.tenderRate ?? item.rate ?? item.unitRate ?? 0);
    return sum + finalQty * rate;
  }, 0);
}

const emptyBoqForm = { sectionNo: "", sectionName: "", tenderedAmount: "" };

function pctOfTender(value, tendered) {
  if (!tendered) return null;
  return ((value / tendered) * 100).toFixed(1) + "%";
}

function varianceColor(variance) {
  if (Math.abs(variance) < 0.005) return t.color.inkMuted;
  // For BOQ: positive = est over tender (bad), negative = est under tender (good)
  return variance > 0 ? t.color.danger : t.color.success;
}

// ═════════════════════════════════════════════════════════════════════════════
// SPARKBAR — visual weight indicator, unique to BOQ register
// ═════════════════════════════════════════════════════════════════════════════
const SparkBar = memo(function SparkBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{
      height: 5, background: t.color.surfaceSunken,
      borderRadius: 99, overflow: "hidden", minWidth: 64,
    }}>
      <div style={{
        height: "100%", width: `${pct}%`,
        background: color || t.color.primary,
        borderRadius: 99,
        transition: `width ${t.motion.slow}`,
      }} />
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// VARIANCE ALERT — banner at top when |variance| > 10% of tender
// ═════════════════════════════════════════════════════════════════════════════
const VarianceAlert = memo(function VarianceAlert({ variance, totalTendered }) {
  const isOver = variance > 0;
  const pct = pctOfTender(variance, totalTendered);
  return (
    <div style={{
      padding: "12px 16px", borderRadius: t.radius.md,
      background: isOver ? t.color.dangerSoft : t.color.successSoft,
      border: `1px solid ${isOver ? t.color.dangerBorder : t.color.successBorder}`,
      color: isOver ? t.color.danger : t.color.success,
      fontSize: 12, fontWeight: 600,
      display: "flex", gap: 12, alignItems: "center",
    }}>
      <span style={{
        width: 28, height: 28, borderRadius: "50%",
        background: isOver ? "rgba(199,59,42,0.15)" : "rgba(31,122,77,0.15)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {isOver ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        )}
      </span>
      <span>
        Engineer's estimate is <strong style={{ fontFamily: t.font.mono }}>{fmtVariance(variance)}</strong>
        {pct && <> (<strong style={{ fontFamily: t.font.mono }}>{Math.abs(pct.replace("%", ""))}%</strong>) vs tender.</>}
        {" "}{isOver
          ? "Review final estimated quantities — project may be tracking over tender."
          : "Project is tracking below tender — confirm final quantities are complete."}
      </span>
    </div>
  );
});

// ─── Page-specific icons ─────────────────────────────────────────────────────
const I = {
  add:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  save:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  cancel: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  edit:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  info:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  warn:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function BOQSection({
  currentProjectId,
  variations = [],
  soqItems = [],
}) {
  // ── State (preserved exactly) ─────────────────────────────────────────────
  const [boqForm,          setBoqForm]          = useState(emptyBoqForm);
  const [sections,         setSections]         = useState([]);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [searchText,       setSearchText]       = useState("");
  const [sortBy,           setSortBy]           = useState("sectionNo_asc");
  const [loading,          setLoading]          = useState(true);
  const [saveError,        setSaveError]        = useState("");

  // ── Firestore listener (preserved exactly) ────────────────────────────────
  useEffect(() => {
    if (!currentProjectId) { setSections([]); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "projects", currentProjectId, "boqSections"),
      (snap) => {
        setSections(
          snap.docs
            .map((d) => {
              const data = d.data() || {};
              return {
                ...data, id: d.id, projectId: currentProjectId,
                sectionNo:         data.sectionNo         || "",
                sectionName:       data.sectionName       || "",
                tenderedAmount:    data.tenderedAmount     || 0,
                engineersEstimate: data.engineersEstimate  || 0,
              };
            })
            .sort((a, b) =>
              String(a.sectionNo || "").localeCompare(String(b.sectionNo || ""), undefined, {
                numeric: true, sensitivity: "base",
              })
            )
        );
        setLoading(false);
      },
      (err) => {
        console.error("BOQ sections load error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [currentProjectId]);

  // ── Omission totals by section key (preserved exactly) ───────────────────
  const omissionTotalsBySection = useMemo(() => {
    const totals = {};
    variations
      .filter((v) => v.projectId === currentProjectId)
      .filter(isApprovedVariation)
      .filter((v) => isOmissionVariation(v) || numberOrZero(v.omittedWorkAmount) > 0)
      .forEach((v) => {
        const key =
          normalizeBoqValue(v.boqSectionId)      ||
          normalizeBoqValue(v.boqSectionReference)||
          normalizeBoqValue(v.boqSectionLabel)   ||
          normalizeBoqValue(v.boqSectionNo)      ||
          normalizeBoqValue(v.boqSectionName)    ||
          normalizeBoqValue(v.boqSection)        ||
          normalizeBoqValue(v.sectionNo)         ||
          normalizeBoqValue(v.sectionName);
        if (!key) return;
        const amt = getVariationOmissionAmount(v);
        if (!amt) return;
        totals[key] = (totals[key] || 0) + amt;
      });
    return totals;
  }, [variations, currentProjectId]);

  // ── Phase-1 perf: debounced search + split enrichment / filter memos ─────
  const debouncedSearch = useDebouncedValue(searchText, 180);

  // Heavy enrichment — only re-runs when underlying data changes
  const enrichedSections = useMemo(() => {
    return sections.map((section) => {
      const keys = buildPossibleBoqKeys(
        section.id,
        `${section.sectionNo} ${section.sectionName}`,
        `${section.sectionNo} - ${section.sectionName}`,
        section.sectionNo,
        section.sectionName,
        `${section.sectionName} ${section.sectionNo}`,
      );
      const tenderedAmount    = numberOrZero(section.tenderedAmount);
      const engineersEstimate = getSoqEngineerEstimateForSection(section, soqItems);
      const omissionAmount    = keys.reduce((found, k) => found > 0 ? found : numberOrZero(omissionTotalsBySection[k]), 0);
      const revisedAmount     = engineersEstimate - omissionAmount;
      const varianceVsTender  = engineersEstimate - tenderedAmount;
      return { ...section, tenderedAmount, engineersEstimate, omissionAmount, revisedAmount, varianceVsTender };
    });
  }, [sections, omissionTotalsBySection, soqItems]);

  // Cheap filter + sort
  const sectionRows = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const searched = needle
      ? enrichedSections.filter((row) =>
          [row.sectionNo, row.sectionName].join(" ").toLowerCase().includes(needle)
        )
      : enrichedSections;

    return [...searched].sort((a, b) => {
      if (sortBy === "sectionNo_desc") return String(b.sectionNo || "").localeCompare(String(a.sectionNo || ""), undefined, { numeric: true, sensitivity: "base" });
      if (sortBy === "value_desc")     return b.tenderedAmount - a.tenderedAmount;
      if (sortBy === "value_asc")      return a.tenderedAmount - b.tenderedAmount;
      if (sortBy === "variance_desc")  return Math.abs(b.varianceVsTender) - Math.abs(a.varianceVsTender);
      return String(a.sectionNo || "").localeCompare(String(b.sectionNo || ""), undefined, { numeric: true, sensitivity: "base" });
    });
  }, [enrichedSections, debouncedSearch, sortBy]);

  // ── Summary totals ────────────────────────────────────────────────────────
  const summary = useMemo(() => ({
    totalSections:          sectionRows.length,
    totalTendered:          sectionRows.reduce((s, r) => s + r.tenderedAmount,    0),
    totalEngineersEstimate: sectionRows.reduce((s, r) => s + r.engineersEstimate, 0),
    totalOmissions:         sectionRows.reduce((s, r) => s + r.omissionAmount,    0),
    totalRevised:           sectionRows.reduce((s, r) => s + r.revisedAmount,     0),
    sectionsWithOmissions:  sectionRows.filter((r) => r.omissionAmount > 0).length,
    maxTenderedAmount:      Math.max(...sectionRows.map((r) => r.tenderedAmount), 0),
  }), [sectionRows]);

  const overallVariance = summary.totalEngineersEstimate - summary.totalTendered;
  const varianceIsSignificant =
    summary.totalTendered > 0 && Math.abs(overallVariance) > summary.totalTendered * 0.1;

  // ── CRUD handlers (preserved exactly) ─────────────────────────────────────
  async function handleSaveSection() {
    setSaveError("");
    if (!currentProjectId || !boqForm.sectionNo.trim() || !boqForm.sectionName.trim()) {
      setSaveError("Section number and section name are both required.");
      return;
    }
    const canonicalId = buildBoqDocId(boqForm.sectionNo, boqForm.sectionName);
    const basePayload = {
      projectId:      currentProjectId,
      sectionNo:      boqForm.sectionNo.trim(),
      sectionName:    boqForm.sectionName.trim(),
      tenderedAmount: boqForm.tenderedAmount,
    };

    async function removeDuplicates(savedId) {
      const snap = await getDocs(collection(db, "projects", currentProjectId, "boqSections"));
      const targetNo   = normalizeBoqValue(basePayload.sectionNo);
      const targetName = normalizeBoqValue(basePayload.sectionName);
      const dupes = snap.docs.filter((d) => {
        const data = d.data() || {};
        return d.id !== savedId &&
          normalizeBoqValue(data.sectionNo)   === targetNo &&
          normalizeBoqValue(data.sectionName) === targetName;
      });
      await Promise.all(dupes.map((d) =>
        deleteDoc(doc(db, "projects", currentProjectId, "boqSections", d.id))
      ));
    }

    try {
      await setDoc(
        doc(db, "projects", currentProjectId, "boqSections", canonicalId),
        { id: canonicalId, ...basePayload },
        { merge: true }
      );

      if (editingSectionId && editingSectionId !== canonicalId) {
        try {
          await deleteDoc(doc(db, "projects", currentProjectId, "boqSections", editingSectionId));
        } catch (err) {
          console.error("Error removing previous BOQ section:", err);
        }
      }

      await removeDuplicates(canonicalId);
      setBoqForm(emptyBoqForm);
      setEditingSectionId(null);
    } catch (err) {
      console.error("BOQ section save error:", err);
      setSaveError("Save failed. Please check your connection and try again.");
    }
  }

  function handleEditSection(row) {
    setBoqForm({
      sectionNo:      row.sectionNo    || "",
      sectionName:    row.sectionName  || "",
      tenderedAmount: String(row.tenderedAmount || ""),
    });
    setEditingSectionId(row.id);
    setSaveError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleDeleteSection(row) {
    if (!currentProjectId || !row) return;
    try {
      const snap        = await getDocs(collection(db, "projects", currentProjectId, "boqSections"));
      const targetId    = String(row.id    || "").trim();
      const targetNo    = normalizeBoqValue(row.sectionNo);
      const targetName  = normalizeBoqValue(row.sectionName);
      const canonicalId = buildBoqDocId(row.sectionNo, row.sectionName);

      const toDelete = snap.docs.filter((d) => {
        const data      = d.data() || {};
        const fId       = String(d.id      || "").trim();
        const legacyId  = String(data.id   || "").trim();
        const sNo       = normalizeBoqValue(data.sectionNo);
        const sName     = normalizeBoqValue(data.sectionName);
        const idMatch   =
          (targetId    && (fId === targetId    || legacyId === targetId))   ||
          (canonicalId && (fId === canonicalId || legacyId === canonicalId));
        const secMatch  =
          (targetNo   && sNo   === targetNo)   ||
          (targetName && sName === targetName);
        return idMatch || secMatch;
      });

      await Promise.all(toDelete.map((d) =>
        deleteDoc(doc(db, "projects", currentProjectId, "boqSections", d.id))
      ));

      if (editingSectionId === row.id || editingSectionId === canonicalId) {
        setBoqForm(emptyBoqForm);
        setEditingSectionId(null);
      }
    } catch (err) {
      console.error("BOQ section delete error:", err);
    }
  }

  function cancelEdit() {
    setBoqForm(emptyBoqForm);
    setEditingSectionId(null);
    setSaveError("");
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <section style={{ position: "relative", display: "grid", gap: 18, fontFamily: t.font.body }}>
      <BlueprintGrid />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 18 }}>

        {/* ───── PAGE HEADER ───── */}
        <Reveal delay={0}>
          <Card padded={false} style={{ borderRadius: 14, position: "relative" }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 40%, ${t.color.primary} 100%)`,
            }} />
            <div style={{ padding: "24px 26px", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Eyebrow>Commercial Baseline</Eyebrow>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                  <span style={{
                    fontFamily: t.font.mono, fontSize: 11, fontWeight: 600,
                    color: t.color.inkMuted, letterSpacing: "0.02em",
                  }}>{currentProjectId || "—"}</span>
                  {loading && (
                    <>
                      <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                      <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
                        textTransform: "uppercase", color: t.color.inkSubtle,
                      }}>Loading…</span>
                    </>
                  )}
                </div>
                <h1 style={{
                  margin: 0, fontFamily: t.font.display,
                  fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1,
                  color: t.color.ink,
                }}>BOQ Register</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5, maxWidth: 720 }}>
                  Manage the section-level commercial baseline. Engineer's estimates link
                  automatically from the SOQ register using final estimated quantity × tender rate.
                </p>
              </div>
              {editingSectionId && (
                <Button variant="subtle" onClick={cancelEdit} icon={I.cancel}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </Card>
        </Reveal>

        {/* ───── KPI STRIP ───── */}
        <Reveal delay={120}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}>
            <Stat
              label="BOQ Sections"
              rawValue={summary.totalSections}
              format={(v) => Math.round(v)}
              value={summary.totalSections}
              accent={t.color.primary}
              delay={80}
              helper={loading ? "Loading…" : "Sections in register"}
            />
            <Stat
              label="Total Tendered"
              rawValue={summary.totalTendered}
              format={(v) => fmtCurrency(v)}
              value={fmtCurrency(summary.totalTendered)}
              accent={t.color.primary}
              delay={140}
              helper="Sum of tendered amounts"
            />
            <Stat
              label="Engineer's Estimate"
              rawValue={summary.totalEngineersEstimate}
              format={(v) => fmtCurrency(v)}
              value={fmtCurrency(summary.totalEngineersEstimate)}
              accent={t.color.info}
              delay={200}
              helper="From SOQ final qty × rate"
            />
            <Stat
              label="VO Omissions"
              rawValue={summary.totalOmissions}
              format={(v) => fmtCurrency(v)}
              value={fmtCurrency(summary.totalOmissions)}
              accent={t.color.danger}
              delay={260}
              helper={summary.sectionsWithOmissions > 0
                ? `${summary.sectionsWithOmissions} section${summary.sectionsWithOmissions !== 1 ? "s" : ""} affected`
                : "No approved omissions"}
            />
            <Stat
              label="Revised Total"
              rawValue={summary.totalRevised}
              format={(v) => fmtCurrency(v)}
              value={fmtCurrency(summary.totalRevised)}
              accent={t.color.accent}
              delay={320}
              helper={overallVariance !== 0
                ? `${fmtVariance(overallVariance)} vs tender`
                : "Aligned with tender"}
            />
          </div>
        </Reveal>

        {/* ───── VARIANCE ALERT (only when significant) ───── */}
        {varianceIsSignificant && (
          <Reveal delay={180}>
            <VarianceAlert variance={overallVariance} totalTendered={summary.totalTendered} />
          </Reveal>
        )}

        {/* ───── SECTION CAPTURE FORM ───── */}
        <Reveal delay={220}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow>{editingSectionId ? "Editing record" : "New record"}</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: t.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
              }}>
                {editingSectionId ? "Edit BOQ Section" : "Add BOQ Section"}
              </h3>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5 }}>
                Section number and name must match exactly what is in the SOQ register so the
                engineer's estimate links correctly.
              </p>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              <FormGroupDivider>Section Identity</FormGroupDivider>

              <FormRow label="Section Number *" helper="Required. Numeric or alphanumeric identifier (e.g. 'A' or '3.2').">
                <Input
                  mono
                  value={boqForm.sectionNo}
                  placeholder="e.g. A or 3.2"
                  invalid={!!saveError && !boqForm.sectionNo.trim()}
                  onChange={(e) => { setBoqForm({ ...boqForm, sectionNo: e.target.value }); setSaveError(""); }}
                />
              </FormRow>

              <FormRow label="Section Name *" helper="Required. Descriptive label (e.g. 'Earthworks', 'Pipelines').">
                <Input
                  value={boqForm.sectionName}
                  placeholder="e.g. Earthworks"
                  invalid={!!saveError && !boqForm.sectionName.trim()}
                  onChange={(e) => { setBoqForm({ ...boqForm, sectionName: e.target.value }); setSaveError(""); }}
                />
              </FormRow>

              <FormRow label="Tendered Amount (ZAR)" helper="The original tendered total for this section. Excludes VAT and contingencies.">
                <Input
                  type="number"
                  placeholder="0"
                  value={boqForm.tenderedAmount}
                  onChange={(e) => setBoqForm({ ...boqForm, tenderedAmount: e.target.value })}
                />
              </FormRow>

              {/* Engineer's estimate explainer */}
              <div style={{
                marginTop: 12, padding: "12px 14px", borderRadius: t.radius.sm,
                background: t.color.infoSoft, border: `1px solid #9CC4DF`,
                fontSize: 12, color: t.color.info,
                display: "flex", gap: 10, alignItems: "flex-start",
              }}>
                <span style={{ color: t.color.info, marginTop: 1, flexShrink: 0 }}>{I.info}</span>
                <span>
                  <strong>Engineer's Estimate</strong> is calculated automatically from the SOQ register
                  using final estimated quantities × tender rates for SOQ items whose section number or
                  name matches this BOQ section. No manual entry required.
                </span>
              </div>

              {/* Save error inline */}
              {saveError && (
                <div style={{
                  marginTop: 10, padding: "10px 12px", borderRadius: t.radius.sm,
                  background: t.color.dangerSoft, color: t.color.danger,
                  fontSize: 12, fontWeight: 600,
                  border: `1px solid ${t.color.dangerBorder}`,
                  display: "flex", gap: 8, alignItems: "center",
                }}>
                  <span style={{ flexShrink: 0 }}>{I.warn}</span>
                  <span>{saveError}</span>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
                <Button variant="accent" onClick={handleSaveSection} icon={editingSectionId ? I.save : I.add}>
                  {editingSectionId ? "Update BOQ Section" : "Save BOQ Section"}
                </Button>
                {editingSectionId && (
                  <Button variant="subtle" onClick={cancelEdit} icon={I.cancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Reveal>

        {/* ───── TABLE CARD ───── */}
        <Reveal delay={300}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 16px",
              display: "flex", alignItems: "flex-end", justifyContent: "space-between",
              gap: 16, flexWrap: "wrap",
            }}>
              <div>
                <Eyebrow>Register</Eyebrow>
                <h3 style={{
                  margin: "6px 0 0", fontFamily: t.font.display,
                  fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
                }}>BOQ Sections</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: t.color.inkSubtle }}>
                  Showing <strong style={{ fontFamily: t.font.mono, color: t.color.ink }}>{sectionRows.length}</strong> of{" "}
                  <strong style={{ fontFamily: t.font.mono, color: t.color.ink }}>{sections.length}</strong> sections
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ display: "grid", gap: 4, minWidth: 220 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: t.color.inkSubtle,
                  }}>Search</span>
                  <Input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Section number or name…"
                  />
                </div>
                <div style={{ display: "grid", gap: 4, minWidth: 220 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: t.color.inkSubtle,
                  }}>Sort</span>
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="sectionNo_asc">Section No (A → Z)</option>
                    <option value="sectionNo_desc">Section No (Z → A)</option>
                    <option value="value_desc">Tendered (High → Low)</option>
                    <option value="value_asc">Tendered (Low → High)</option>
                    <option value="variance_desc">Variance (Largest first)</option>
                  </Select>
                </div>
              </div>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              {loading ? (
                <Empty title="Loading BOQ sections…" message="Fetching the latest data from the project register." />
              ) : sectionRows.length === 0 ? (
                <Empty
                  title="No BOQ sections found"
                  message="Add a section using the form above, or import the SOQ schedule first to seed the register."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Section No</Th>
                      <Th>Section Name</Th>
                      <Th>Weight</Th>
                      <Th align="right">Tendered</Th>
                      <Th align="right">Engineer's Estimate</Th>
                      <Th align="right">Variance</Th>
                      <Th align="right">VO Omissions</Th>
                      <Th align="right">Revised Total</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionRows.map((row) => {
                      const hasOmission = row.omissionAmount > 0;
                      const varPos      = row.varianceVsTender > 0;
                      const varSig      = row.tenderedAmount > 0 &&
                        Math.abs(row.varianceVsTender) > row.tenderedAmount * 0.05;
                      const isEditing   = editingSectionId === row.id;

                      return (
                        <tr
                          key={row.id}
                          style={{
                            background: isEditing ? t.color.accentSoft : undefined,
                          }}
                        >
                          {/* Section No */}
                          <Td mono weight={700}>{row.sectionNo || "—"}</Td>

                          {/* Section Name + omission tag */}
                          <Td>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontWeight: 500 }}>{row.sectionName || "—"}</span>
                              {hasOmission && <Badge variant="danger" size="sm">VO Omission</Badge>}
                            </div>
                          </Td>

                          {/* Weight sparkbar */}
                          <Td style={{ minWidth: 90 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <SparkBar
                                value={row.tenderedAmount}
                                max={summary.maxTenderedAmount}
                                color={t.color.primary}
                              />
                              <span style={{
                                fontSize: 10, fontFamily: t.font.mono,
                                color: t.color.inkSubtle, letterSpacing: "-0.005em",
                              }}>
                                {pctOfTender(row.tenderedAmount, summary.totalTendered) || "—"}
                              </span>
                            </div>
                          </Td>

                          {/* Tendered */}
                          <Td align="right" mono weight={600}>
                            {fmtCurrency(row.tenderedAmount)}
                          </Td>

                          {/* Engineer's estimate */}
                          <Td align="right" mono weight={600}
                            style={{ color: row.engineersEstimate > 0 ? t.color.info : undefined }}>
                            {row.engineersEstimate > 0
                              ? fmtCurrency(row.engineersEstimate)
                              : <span style={{ color: t.color.inkSubtle, fontWeight: 400, fontFamily: t.font.body }}>No SOQ data</span>}
                          </Td>

                          {/* Variance */}
                          <Td align="right" mono
                            weight={varSig ? 700 : 500}
                            style={{
                              color: row.engineersEstimate > 0
                                ? varianceColor(row.varianceVsTender)
                                : t.color.inkSubtle,
                            }}>
                            {row.engineersEstimate > 0 ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                                {fmtVariance(row.varianceVsTender)}
                                {varSig && (
                                  <span style={{ fontSize: 11 }}>{varPos ? "▲" : "▼"}</span>
                                )}
                              </span>
                            ) : "—"}
                          </Td>

                          {/* VO Omissions */}
                          <Td align="right" mono
                            weight={hasOmission ? 700 : 500}
                            style={{ color: hasOmission ? t.color.danger : t.color.inkSubtle }}>
                            {hasOmission ? fmtCurrency(row.omissionAmount) : "—"}
                          </Td>

                          {/* Revised Total */}
                          <Td align="right" mono weight={700}>
                            {row.engineersEstimate > 0 || hasOmission
                              ? fmtCurrency(row.revisedAmount)
                              : <span style={{ color: t.color.inkSubtle, fontWeight: 400 }}>—</span>}
                          </Td>

                          {/* Actions */}
                          <Td align="right">
                            <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                              <Button size="sm" variant="subtle" onClick={() => handleEditSection(row)} icon={I.edit}>
                                Edit
                              </Button>
                              <Button size="sm" variant="danger" onClick={() => handleDeleteSection(row)} icon={I.trash}>
                                Delete
                              </Button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Totals footer */}
                  {sectionRows.length > 0 && (
                    <tfoot>
                      <tr style={{ background: t.color.surfaceAlt }}>
                        <td colSpan={3} style={{
                          padding: "12px 12px", textAlign: "left",
                          fontSize: 12, fontWeight: 700, color: t.color.ink,
                          borderTop: `2px solid ${t.color.borderStrong}`,
                          fontFamily: t.font.body,
                          letterSpacing: "-0.005em",
                        }}>
                          Totals — {summary.totalSections} section{summary.totalSections !== 1 ? "s" : ""}
                        </td>
                        <td style={{
                          padding: "12px 12px", textAlign: "right",
                          fontSize: 13, fontWeight: 700, color: t.color.ink,
                          borderTop: `2px solid ${t.color.borderStrong}`,
                          fontFamily: t.font.mono, fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.01em",
                        }}>
                          {fmtCurrency(summary.totalTendered)}
                        </td>
                        <td style={{
                          padding: "12px 12px", textAlign: "right",
                          fontSize: 13, fontWeight: 700, color: t.color.info,
                          borderTop: `2px solid ${t.color.borderStrong}`,
                          fontFamily: t.font.mono, fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.01em",
                        }}>
                          {fmtCurrency(summary.totalEngineersEstimate)}
                        </td>
                        <td style={{
                          padding: "12px 12px", textAlign: "right",
                          fontSize: 13, fontWeight: 700,
                          color: varianceColor(overallVariance),
                          borderTop: `2px solid ${t.color.borderStrong}`,
                          fontFamily: t.font.mono, fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.01em",
                        }}>
                          {fmtVariance(overallVariance)}
                        </td>
                        <td style={{
                          padding: "12px 12px", textAlign: "right",
                          fontSize: 13, fontWeight: 700,
                          color: summary.totalOmissions > 0 ? t.color.danger : t.color.inkSubtle,
                          borderTop: `2px solid ${t.color.borderStrong}`,
                          fontFamily: t.font.mono, fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.01em",
                        }}>
                          {summary.totalOmissions > 0 ? fmtCurrency(summary.totalOmissions) : "—"}
                        </td>
                        <td style={{
                          padding: "12px 12px", textAlign: "right",
                          fontSize: 13, fontWeight: 700, color: t.color.accent,
                          borderTop: `2px solid ${t.color.borderStrong}`,
                          fontFamily: t.font.mono, fontVariantNumeric: "tabular-nums",
                          letterSpacing: "-0.01em",
                        }}>
                          {fmtCurrency(summary.totalRevised)}
                        </td>
                        <td style={{
                          borderTop: `2px solid ${t.color.borderStrong}`,
                        }} />
                      </tr>
                    </tfoot>
                  )}
                </Table>
              )}
            </div>
          </Card>
        </Reveal>

      </div>
    </section>
  );
}

export default memo(BOQSection);